import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react'
import { GlobalState, SystemHealth, Alert, Client, Campaign, Mailbox } from '@/lib/types'
import { supabase, isSupabaseConfigured } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

interface DashboardContextType {
  state: GlobalState
  setSelectedClient: (clientId: string | 'all') => void
  addAlert: (alert: Alert) => void
  resolveAlert: (alertId: string) => void
  updateSystemHealth: (health: Partial<SystemHealth>) => void
  refreshCache: (type: 'clients' | 'campaigns' | 'mailboxes') => Promise<void>
}

const DashboardContext = createContext<DashboardContextType | undefined>(undefined)

type Action =
  | { type: 'SET_SELECTED_CLIENT'; payload: string | 'all' }
  | { type: 'ADD_ALERT'; payload: Alert }
  | { type: 'RESOLVE_ALERT'; payload: string }
  | { type: 'UPDATE_SYSTEM_HEALTH'; payload: Partial<SystemHealth> }
  | { type: 'SET_CACHE'; payload: { type: 'clients' | 'campaigns' | 'mailboxes'; data: any[] } }

/** Jamie UX feedback item 4 (2026-08-05): the selected client used to
 * live only in this in-memory reducer, so every page refresh reset the
 * scope to "All Clients". Persist it per-browser like the dashboard's
 * period/custom-range already are. Client-role users are unaffected —
 * the lock effect below overrides any restored value with their own
 * client_id, and setSelectedClient (the only writer) is admin-only. */
const LS_SELECTED_CLIENT_KEY = 'newux_selected_client'

function readPersistedClient(): string | 'all' {
  if (typeof window === 'undefined') return 'all'
  try {
    const v = window.localStorage.getItem(LS_SELECTED_CLIENT_KEY)
    return v && v.trim() ? v : 'all'
  } catch {
    // localStorage disabled (Safari private mode, etc.)
    return 'all'
  }
}

const initialState: GlobalState = {
  selectedClient: 'all',
  alerts: [],
  systemHealth: {
    apiStatus: 'healthy',
    lastSync: new Date(),
    pendingOperations: 0
  },
  cache: {
    clients: { data: [], lastFetch: new Date(0) },
    campaigns: { data: [], lastFetch: new Date(0) },
    mailboxes: { data: [], lastFetch: new Date(0) }
  }
}

function dashboardReducer(state: GlobalState, action: Action): GlobalState {
  switch (action.type) {
    case 'SET_SELECTED_CLIENT':
      return { ...state, selectedClient: action.payload }
    case 'ADD_ALERT':
      return { ...state, alerts: [action.payload, ...state.alerts] }
    case 'RESOLVE_ALERT':
      return {
        ...state,
        alerts: state.alerts.map(alert =>
          alert.id === action.payload ? { ...alert, status: 'resolved' as const } : alert
        )
      }
    case 'UPDATE_SYSTEM_HEALTH':
      return {
        ...state,
        systemHealth: { ...state.systemHealth, ...action.payload }
      }
    case 'SET_CACHE':
      return {
        ...state,
        cache: {
          ...state.cache,
          [action.payload.type]: {
            data: action.payload.data,
            lastFetch: new Date()
          }
        }
      }
    default:
      return state
  }
}

export function DashboardProvider({ children }: { children: React.ReactNode }) {
  // Lazy third-arg init so localStorage is read once per mount, not on
  // every render (item 4 — restore the last selected client scope).
  const [state, dispatch] = useReducer(dashboardReducer, initialState, (s) => ({
    ...s,
    selectedClient: readPersistedClient(),
  }))
  const { profile, isClient } = useAuth()

  // Lock selectedClient to the user's assigned client for client-role users
  useEffect(() => {
    if (isClient && profile?.client_id) {
      dispatch({ type: 'SET_SELECTED_CLIENT', payload: profile.client_id })
    }
  }, [isClient, profile?.client_id])

  const setSelectedClient = useCallback((clientId: string | 'all') => {
    // Client-role users cannot change their selected client
    if (isClient) {
      console.warn('[DashboardContext] Client users cannot switch workspaces')
      return
    }
    dispatch({ type: 'SET_SELECTED_CLIENT', payload: clientId })
    // Persist so a page refresh keeps the chosen scope (item 4).
    try {
      window.localStorage.setItem(LS_SELECTED_CLIENT_KEY, clientId)
    } catch {
      // localStorage disabled — selection just won't survive refresh
    }
  }, [isClient])

  const addAlert = (alert: Alert) => {
    dispatch({ type: 'ADD_ALERT', payload: alert })
  }

  const resolveAlert = (alertId: string) => {
    dispatch({ type: 'RESOLVE_ALERT', payload: alertId })
  }

  const updateSystemHealth = (health: Partial<SystemHealth>) => {
    dispatch({ type: 'UPDATE_SYSTEM_HEALTH', payload: health })
  }

  const refreshCache = async (type: 'clients' | 'campaigns' | 'mailboxes') => {
    if (!isSupabaseConfigured() || !supabase) {
      console.log('Supabase not configured yet, skipping cache refresh')
      return
    }

    try {
      const { data, error } = await supabase.from(type).select('*')
      if (error) throw error
      dispatch({ type: 'SET_CACHE', payload: { type, data } })
    } catch (error) {
      console.error(`Failed to refresh ${type} cache:`, error)
    }
  }

  // Initialize cache on mount only if Supabase is configured
  useEffect(() => {
    if (isSupabaseConfigured()) {
      refreshCache('clients')
      refreshCache('campaigns')
      refreshCache('mailboxes')
    }
  }, [])

  // Set up real-time subscriptions only if Supabase is configured
  useEffect(() => {
    if (!isSupabaseConfigured() || !supabase) {
      return
    }

    const alertsSubscription = supabase
      .channel('alerts')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'alerts' }, 
        (payload) => {
          addAlert(payload.new as Alert)
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(alertsSubscription)
    }
  }, [])

  const value: DashboardContextType = {
    state,
    setSelectedClient,
    addAlert,
    resolveAlert,
    updateSystemHealth,
    refreshCache
  }

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  )
}

export function useDashboard() {
  const context = useContext(DashboardContext)
  if (context === undefined) {
    throw new Error('useDashboard must be used within a DashboardProvider')
  }
  return context
}
